"use client";

import React, { useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { useFormContext, Controller } from "react-hook-form";
import GeneratorPage from "../GeneratorPage";
import { useLocaleNavigation } from '@/lib/navigation';
import { CATEGORIES } from "@/constants/categories";
import { Zap, Copy, Check } from "lucide-react";
import { Input, Button } from "@/components/design-system";
import { generateGameNames } from "@/lib/gameNameGenerator";

export default function GameNamesGenerator() {
    const t = useTranslations("game-names");
    const [generated, setGenerated] = useState<string[]>([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [copied, setCopied] = useState(false);

    // Initial default values
    const defaultValues = {
        count: 10,
    };

    const handleGenerate = (e: React.FormEvent, data?: any) => {
        if (e && e.preventDefault) e.preventDefault();
        setIsGenerating(true);

        // Use data from React Hook Form or fallback to defaults
        const formData = data || defaultValues;

        // Simulate network delay for better UX
        setTimeout(() => {
            const names = generateGameNames({
                count: Number(formData.count),
                style: 'any'
            });
            setGenerated(names);
            setIsGenerating(false);
        }, 500);
    };

    const handleCopyAll = async () => {
        await navigator.clipboard.writeText(generated.join("\n"));
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
    };

    const formConfig = {
        values: defaultValues,
        onChange: () => { }, // Handled by react-hook-form internally in GeneratorPage
        fields: [
            {
                key: "count",
                label: t("form.count"),
                type: "number" as const,
                placeholder: t("form.countPlaceholder"),
                validation: {
                    min: 1,
                    max: 50,
                },
                defaultValue: 10,
            },
        ],
    };

    const customResultsRenderer = () => {
        if (!generated || generated.length === 0) {
            return (
                <div className="text-center text-gray-500 py-8">
                    {t("results.noResults")}
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{t("results.title")}</h3>
                    <Button type="button" size="sm" variant="outline" onClick={handleCopyAll}>
                        {copied ? (
                            <>
                                <Check className="mr-2 h-4 w-4" />
                                {t("results.copyAllSuccess")}
                            </>
                        ) : (
                            <>
                                <Copy className="mr-2 h-4 w-4" />
                                {t("results.copyAll")}
                            </>
                        )}
                    </Button>
                </div>
                <div className="space-y-2">
                    {generated.map((name, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-500 transition-colors group">
                            <span className="font-medium text-gray-900">{name}</span>
                            <button
                                onClick={() => navigator.clipboard.writeText(name)}
                                className="p-2 text-gray-400 hover:text-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                title={t("form.copyButton")}
                            >
                                <Copy className="h-4 w-4" />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    // Form Content Component to access context
    const FormContent = () => {
        const { control } = useFormContext();
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block mb-1 font-medium">{t("form.count")}</label>
                    <Controller
                        name="count"
                        control={control}
                        render={({ field }) => (
                            <Input
                                type="number"
                                min={1}
                                max={50}
                                value={field.value}
                                onChange={(e) => field.onChange(Number(e.target.value))}
                                placeholder={t("form.countPlaceholder")}
                            />
                        )}
                    />
                </div>
            </div>
        );
    };

    const { getNavigationPath } = useLocaleNavigation();
    const locale = useLocale();

    // Calculate similar generators
    const category = CATEGORIES.entertainment;
    const similarGenerators = category.generators
        .filter(g => g.id !== 'game-names')
        .slice(0, 3) // Show first 3 alternatives
        .map(g => {
            const nameKey = `similarHelpers.${g.id}.name` as any;
            const descKey = `similarHelpers.${g.id}.description` as any;

            // Use t.has() to check if the key exists before translating
            const name = t.has(nameKey) ? t(nameKey) : (g.name[locale as keyof typeof g.name] || g.name['en']);
            const description = t.has(descKey) ? t(descKey) : (g.description[locale as keyof typeof g.description] || g.description['en']);

            return {
                id: g.id,
                name,
                description,
                icon: g.icon,
                href: getNavigationPath(g.id),
                category: category.name['en']
            };
        });

    return (
        <GeneratorPage
            title={t("title")}
            description={t("description")}
            icon={<Zap className="h-4 w-4" />}
            category={CATEGORIES.entertainment}
            generatedData={generated.join("\n")}
            isGenerating={isGenerating}
            onGenerate={handleGenerate}
            formConfig={formConfig}
            resultsRenderer={customResultsRenderer()}
            generateButtonText={t("form.generateButton")}
            faqs={t.raw("faqs")}
            similarGenerators={similarGenerators}
        >
            <FormContent />
        </GeneratorPage>
    );
}

